import { CompanyWorker } from "model/Company";
import { CompanyGig } from "model/Gig";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { ButtonComponent } from "../../general/button/button.component";
import { ChooseWorkersModal } from "../../organizationalStructure/chooseWorkersModal.component";
import { WorkerDropdown } from "../../salary/workerDropDown.component";
import { ChooseWorkersContainer } from "./createGigModal.styled";
import { WorkerList } from "./workerList.component";

type Props = {
  setSelectedWorkers: React.Dispatch<React.SetStateAction<any[]>>;
  selectedWorkers: any[];
  gigData?: CompanyGig;
};

export const ScheduleGigForm: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [selectedWorker, setSelectedWorker] = useState<
    CompanyWorker | undefined
  >(undefined);
  const [dropdownResetKey, setDropdownResetKey] = useState(0);
  const [showChooseSeveral, setShowChooseSeveral] = useState(false);
  const [workerListOpen, setWorkerListOpen] = useState(true);

  useEffect(() => {
    const companyId = companyState.company?.id;
    const workerId = selectedWorker?.id;

    if (!companyId || !workerId) return;

    const alreadySelected = props.selectedWorkers.some(
      (w) => String(w.workerId ?? w.id) === String(workerId)
    );
    if (alreadySelected) return;

    let cancelled = false;
    Api()
      .company.getCompanyWorkerV2(companyId, Number(workerId))
      .then((res) => {
        if (cancelled) return;

        let didAdd = false;
        props.setSelectedWorkers((prev) => {
          if (
            prev.some((w) => String(w.workerId ?? w.id) === String(workerId))
          ) {
            return prev;
          }
          didAdd = true;
          return [...prev, res.data];
        });

        if (didAdd) {
          setSelectedWorker(undefined);
          setDropdownResetKey((k) => k + 1);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch company worker:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedWorker?.id, companyState.company?.id, props.selectedWorkers]);

  const addWorkersToList = useCallback(
    (selected: any[]) => {
      setShowChooseSeveral(false);

      props.setSelectedWorkers((prev) => {
        const key = (w: any) => String(w.workerId ?? w.id);
        const existing = new Set(prev.map(key));

        const incoming = selected
          .map((w) => ({ ...w, workerId: w.workerId ?? w.id }))
          .filter((w) => !existing.has(String(w.workerId)));

        return incoming.length ? [...prev, ...incoming] : prev;
      });
    },
    [props.setSelectedWorkers]
  );

  useEffect(() => {
    const apps = props.gigData?.applications;
    const companyId = companyState.company?.id;

    if (!apps || !companyId) return;

    const selectedTeamId =
      props.gigData?.workGroupId != null
        ? String(props.gigData.workGroupId)
        : undefined;

    const existing = new Set(
      props.selectedWorkers.map((w) => String(w.workerId ?? w.id))
    );
    const toFetchIds = apps
      .map((app) => app.worker.id)
      .filter((id) => !existing.has(String(id)));

    if (toFetchIds.length === 0) return;

    let cancelled = false;

    Promise.all(
      toFetchIds.map((id) =>
        Api()
          .company.organizationalStructure.fetchWorkerProfile(companyId, id)
          .then((res) => ({ id, data: res.data }))
          .catch((err) => {
            console.error("Failed to fetch worker profile:", err);
            return null;
          })
      )
    ).then((results) => {
      if (cancelled) return;

      const additions = (
        results.filter(Boolean) as { id: number; data: any }[]
      ).map(({ data }) =>
        selectedTeamId ? { ...data, selectedTeamId } : data
      );

      if (additions.length === 0) return;

      props.setSelectedWorkers((prev) => {
        const prevIds = new Set(prev.map((w) => String(w.workerId ?? w.id)));
        const uniqueAdditions = additions.filter(
          (w) => !prevIds.has(String(w.workerId ?? w.id))
        );
        return uniqueAdditions.length ? [...prev, ...uniqueAdditions] : prev;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [
    props.gigData?.applications,
    props.gigData?.workGroupId,
    companyState.company?.id,
    props.selectedWorkers,
    props.setSelectedWorkers,
  ]);

  return (
    <>
      {!props.gigData && (
        <ChooseWorkersContainer>
          <WorkerDropdown
            key={dropdownResetKey}
            selectedWorker={selectedWorker}
            setSelectedWorker={setSelectedWorker}
            onlyActiveWorkers={true}
            width={260}
          />
          <ButtonComponent
            size={"small"}
            onClick={() => setShowChooseSeveral(true)}
            action
            marginBottom={20}
          >
            {t("CreateShift.ChooseSeveral")}
          </ButtonComponent>
        </ChooseWorkersContainer>
      )}

      <WorkerList
        selectedWorkers={props.selectedWorkers}
        setSelectedWorkers={props.setSelectedWorkers}
        setWorkerListOpen={setWorkerListOpen}
        workerListOpen={workerListOpen}
        gigData={props.gigData}
      />

      {showChooseSeveral && (
        <ChooseWorkersModal
          setShowModal={setShowChooseSeveral}
          showModal={showChooseSeveral}
          onClick={addWorkersToList}
          title={t("CreateShift.ChooseSeveral")}
          info={t("CreateShift.ChooseSeveralInfo")}
          buttonTitle={t("CreateShift.Choose")}
          selected={props.selectedWorkers}
          active
          filter={"InWorkGroup"}
        />
      )}
    </>
  );
};
